import {Graphics, InteractionEvent, Sprite, Text, Texture} from 'pixi.js';
import {IPoint} from '../../../../../interfaces/primitives/IPoint';
import {StaticData} from '../../../../../data/StaticData';
import {Conversion} from '../../../../../utils/Conversion';
import {HoverableObject} from '../../../../../base/HoverableObject';
import {PieMenuItemEvents} from './pie_menu_item/PieMenuItemEvents';
import {PieMenuIcons} from '../PieMenuIcons';
import {TextureUrlLoader} from '../../../../../utils/TextureUrlLoader';
import {TextFactory} from '../../../../../factories/TextFactory';
import {CommandStrategy, SubmenuType} from '../pie_menu_commands/base/CommandStarategy';
import {DynamicData} from '../../../../../data/DynamicData';
import {PlayerData} from '../../../../../data/active_data/field_config/PlayerData';

export class PieMenuItem extends HoverableObject
{
    private graphics: Graphics = new Graphics();
    private readonly cornerRoundDeltaOuter: number;
    private readonly cornerRoundDeltaInner: number;
    private numberText: Text | null = null;
    private centerPoint: IPoint;
    private iconSprite: Sprite | undefined;

    constructor(
        private readonly startCorner: number,
        private readonly endCorner: number,
        private innerRadius: number,
        private outerRadius: number,
        private strategy: CommandStrategy,
        private index: number,
        private isTier2: boolean
    ) {
        super();

        let twoPi: number = Math.PI * 2;
        let outerCircleLength: number = this.outerRadius * twoPi;
        let innerCircleLength: number = this.innerRadius * twoPi;
        let radiusRoundDeltaPartOuter: number = StaticData.pieMenuRoundCornersRadius / outerCircleLength;
        let radiusRoundDeltaPartInner: number = StaticData.pieMenuRoundCornersRadius / innerCircleLength;

        this.cornerRoundDeltaOuter = twoPi * radiusRoundDeltaPartOuter;
        this.cornerRoundDeltaInner = twoPi * radiusRoundDeltaPartInner;

        this.centerPoint = Conversion.polarToOrthogonal(this.startCorner + (this.endCorner - this.startCorner) / 2, this.innerRadius + (this.outerRadius - this.innerRadius) / 2);

        this.addChild(this.graphics);

        if (this.isTier2) {
            switch (this.strategy.command.submenuType) {
                case SubmenuType.NUMBER:
                    this.drawBack(false);
                    this.drawNumber(this.index);
                    break;
                case SubmenuType.PLAYER:
                    let tier2PlayerAlone: boolean = this.isTier2 && this.strategy.getTier2CommandsCount() == 1;
                    this.drawBack(tier2PlayerAlone, tier2PlayerAlone);
                    break;
            }

        } else {
            this.drawBack(false);
            this.drawIcon(this.strategy.command.iconId);
        }
    }

    /**
     * Обработчик события onpointerdown
     * @param e Событие взаимодейтсвия пользователя
     */
    onPointerDown(e: InteractionEvent): void {
        if (this.strategy.command.hasSubmenu && !this.isTier2) {
            if (this.strategy.command.submenuType == SubmenuType.PLAYER && this.strategy.getTier2CommandsCount() == 1) {
                dispatchEvent(new MessageEvent(PieMenuItemEvents.ON_PIE_MENU_POINTER_DOWN, {data: this.getEventData(true)}));
            } else {
                e.stopPropagation();
            }
            return;
        }
        dispatchEvent(new MessageEvent(PieMenuItemEvents.ON_PIE_MENU_POINTER_DOWN, {
            data: this.getEventData()
        }));
    }

    /**
     * Обработчик события pointerover
     * @param e Событие взаимодейтсвия пользователя
     */
    onPointerOver(e: InteractionEvent): void {
        this.drawBack(true, this.isTier2 && this.strategy.command.submenuType == SubmenuType.PLAYER);
        dispatchEvent(new MessageEvent(PieMenuItemEvents.ON_PIE_MENU_POINTER_OVER, {
            data: this.getEventData()
        }));
    }

    /**
     * Обработчик события pointerout
     * @param e Событие взаимодейтсвия пользователя
     */
    onPointerOut(e?: InteractionEvent): void {
        this.drawBack(false);
        dispatchEvent(new MessageEvent(PieMenuItemEvents.ON_PIE_MENU_POINTER_OUT, {
            data: this.getEventData()
        }));
    }

    destroy(): void {
        super.destroy();
    }

    /**
     * Отрисовка геометрии
     * @param color - цвет заливки
     */
    drawBack(hover: boolean, withCircle: boolean = false): void {
        let color: number = hover ? StaticData.pieMenuColorHover : StaticData.pieMenuColorNormal;

        if (this.isTier2) {
            switch (this.strategy.command.submenuType) {
                case SubmenuType.NUMBER:
                    break;
                case SubmenuType.PLAYER:
                    let filterdPlayers: PlayerData[] = DynamicData.getNotMyActivePlayers();
                    if (!filterdPlayers.length) {
                        return;
                    }
                    let playerId: string = filterdPlayers[this.index].id;
                    let colorString = DynamicData.playersColorsMap.get(playerId)!.split('#')[1];
                    color = parseInt('0x' + colorString);

                    this.alpha = hover ? 1 : .9;
                    break;
            }
        }

        //  p2 . . p2_1 _ _ _ _ _ p3_0 . . p3
        //  .    /                     \    .
        //  .  /                         \  .
        //  p2_0                         p3_1
        //   |                             |
        //   |                             |
        //  p1_1                         p4_0
        //  .  \                         /  .
        //  .    \                     /    .
        //  p1 . . p1_0 _ _ _ _ _ p4_1 . . p4

        let p1: IPoint = Conversion.polarToOrthogonal(this.startCorner, this.innerRadius);
        let p1_0: IPoint = Conversion.polarToOrthogonal(this.startCorner + this.cornerRoundDeltaInner, this.innerRadius);
        let p1_1: IPoint = Conversion.polarToOrthogonal(this.startCorner, this.innerRadius + StaticData.pieMenuRoundCornersRadius);

        let p2: IPoint = Conversion.polarToOrthogonal(this.startCorner, this.outerRadius);
        let p2_0: IPoint = Conversion.polarToOrthogonal(this.startCorner, this.outerRadius - StaticData.pieMenuRoundCornersRadius);
        let p2_1: IPoint = Conversion.polarToOrthogonal(this.startCorner + this.cornerRoundDeltaOuter, this.outerRadius);

        let p3: IPoint = Conversion.polarToOrthogonal(this.endCorner, this.outerRadius);
        //let p3_0: IPoint =  Conversion.polarToOrthogonal(this.endCorner - this.cornerRoundDeltaOuter, this.outerRadius);
        let p3_1: IPoint = Conversion.polarToOrthogonal(this.endCorner, this.outerRadius - StaticData.pieMenuRoundCornersRadius);

        let p4: IPoint = Conversion.polarToOrthogonal(this.endCorner, this.innerRadius);
        let p4_0: IPoint = Conversion.polarToOrthogonal(this.endCorner, this.innerRadius + StaticData.pieMenuRoundCornersRadius);
        let p4_1: IPoint = Conversion.polarToOrthogonal(this.endCorner - this.cornerRoundDeltaInner, this.innerRadius);

        this.graphics.clear();
        this.graphics.beginFill(color);
        this.graphics.moveTo(p1_0.x, p1_0.y);
        this.graphics.quadraticCurveTo(p1.x, p1.y, p1_1.x, p1_1.y);
        this.graphics.lineTo(p2_0.x, p2_0.y);
        this.graphics.quadraticCurveTo(p2.x, p2.y, p2_1.x, p2_1.y);
        this.graphics.arc(0, 0, this.outerRadius, this.startCorner + this.cornerRoundDeltaOuter, this.endCorner - this.cornerRoundDeltaOuter, false);
        this.graphics.quadraticCurveTo(p3.x, p3.y, p3_1.x, p3_1.y);
        this.graphics.lineTo(p4_0.x, p4_0.y);
        this.graphics.quadraticCurveTo(p4.x, p4.y, p4_1.x, p4_1.y);
        this.graphics.arc(0, 0, this.innerRadius, this.endCorner - this.cornerRoundDeltaInner, this.startCorner + this.cornerRoundDeltaInner, true);
        if (withCircle) {
            this.graphics.beginFill(0xffffff);
            this.graphics.drawCircle(this.centerPoint.x, this.centerPoint.y, 5);
        }
        this.graphics.endFill();
    }

    private getEventData(firstOfInner: boolean = false): {strategy: CommandStrategy, startCorner: number, isTier2: boolean, commandIndex: number} {
        return {
            strategy: this.strategy,
            startCorner: this.startCorner,
            isTier2: this.isTier2,
            commandIndex: firstOfInner ? 0 : this.index
        };
    }

    /**
     * Отрисовка номера
     * @param index - zero-based индекс элемента
     */
    private drawNumber(index: number) {
        this.numberText = TextFactory.create((index + 1).toString());
        this.addChild(this.numberText);
        this.numberText.anchor.set(0.5);
        this.numberText.position.set(this.centerPoint.x, this.centerPoint.y);
    }

    /**
     * Отрисовка иконки
     * @param iconId - имя файла svg-иконки
     */
    private async drawIcon(iconId: PieMenuIcons) {
        const iconPath = '/images/context-menu/' + iconId + '.svg';
        let iconTexture: Texture | null = await TextureUrlLoader.load(iconPath);
        this.iconSprite = new Sprite(iconTexture!);
        this.iconSprite.anchor.set(.5, .5);
        this.iconSprite.position.set(this.centerPoint.x, this.centerPoint.y);
        this.addChild(this.iconSprite);
    }
}
